"use client";

import { useRouter } from "next/navigation";
import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  defaultLocale,
  isLocale,
  locales,
  normalizeLocale,
  type Locale,
} from "@/lib/i18n";
import {
  LOCALE_FREE_PUBLIC_PREFIXES,
  collapseDoubleLocalePrefix,
  stripLeadingLocaleSegments,
} from "@/lib/locale-path";
import { singleLocaleLocationPaths } from "@/data/locations";
import { useVisiblePathname } from "@/components/useVisiblePathname";

// English-only pages (spec 2.3): switching language goes to that locale's
// homepage instead of a /{locale}/… URL that only 308s back.
const UNLOCALIZED_PREFIXES: string[] = [...LOCALE_FREE_PUBLIC_PREFIXES];
const SYSTEM_PREFIXES = [
  "/admin",
  "/portal",
  "/developer",
  "/developer-login",
  "/client",
  "/insights",
  "/client-login",
  "/tester",
  "/tester-login",
];

function USFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-us">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-us)">
        <rect width="20" height="14" fill="#B22234" />
        <path d="M0 2.15h20M0 4.31h20M0 6.46h20M0 8.62h20M0 10.77h20M0 12.92h20" stroke="#FFFFFF" strokeWidth="1.08" />
        <rect width="8.5" height="7.54" fill="#3C3B6E" />
        <circle cx="2" cy="1.9" r="0.55" fill="#FFFFFF" />
        <circle cx="4.25" cy="1.9" r="0.55" fill="#FFFFFF" />
        <circle cx="6.5" cy="1.9" r="0.55" fill="#FFFFFF" />
        <circle cx="3.12" cy="3.77" r="0.55" fill="#FFFFFF" />
        <circle cx="5.37" cy="3.77" r="0.55" fill="#FFFFFF" />
        <circle cx="2" cy="5.64" r="0.55" fill="#FFFFFF" />
        <circle cx="4.25" cy="5.64" r="0.55" fill="#FFFFFF" />
        <circle cx="6.5" cy="5.64" r="0.55" fill="#FFFFFF" />
      </g>
    </svg>
  );
}

function CAFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-ca">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-ca)">
        <rect width="20" height="14" fill="#D80621" />
        <rect x="5" width="10" height="14" fill="#FFFFFF" />
        <path
          d="M10 2.2l.6 1.8 1.4-.4-.5 1.5 1.7.6-1.5 1.1.7 1.4-1.8-.4-.4 2.2h-.4l-.4-2.2-1.8.4.7-1.4-1.5-1.1 1.7-.6-.5-1.5 1.4.4.6-1.8z"
          fill="#D80621"
        />
      </g>
    </svg>
  );
}

function AUFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-au">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-au)">
        <rect width="20" height="14" fill="#00008B" />
        <g>
          <rect width="10" height="7" fill="#012169" />
          <path d="M0 0l10 7M10 0L0 7" stroke="#FFFFFF" strokeWidth="1.4" />
          <path d="M0 0l10 7M10 0L0 7" stroke="#C8102E" strokeWidth="0.8" />
          <path d="M5 0v7M0 3.5h10" stroke="#FFFFFF" strokeWidth="2.2" />
          <path d="M5 0v7M0 3.5h10" stroke="#C8102E" strokeWidth="1.3" />
        </g>
        <circle cx="5" cy="10.5" r="1.3" fill="#FFFFFF" />
        <circle cx="15" cy="2.5" r="0.65" fill="#FFFFFF" />
        <circle cx="17.2" cy="5.2" r="0.65" fill="#FFFFFF" />
        <circle cx="15" cy="11.5" r="0.65" fill="#FFFFFF" />
        <circle cx="12.8" cy="6.2" r="0.65" fill="#FFFFFF" />
        <circle cx="16" cy="8" r="0.45" fill="#FFFFFF" />
      </g>
    </svg>
  );
}

function GBFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-gb">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-gb)">
        <rect width="20" height="14" fill="#012169" />
        <path d="M0 0l20 14M20 0L0 14" stroke="#FFFFFF" strokeWidth="2.8" />
        <path d="M0 0l20 14M20 0L0 14" stroke="#C8102E" strokeWidth="1.6" />
        <path d="M10 0v14M0 7h20" stroke="#FFFFFF" strokeWidth="4.6" />
        <path d="M10 0v14M0 7h20" stroke="#C8102E" strokeWidth="2.6" />
      </g>
    </svg>
  );
}

function FRFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-fr">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-fr)">
        <rect width="20" height="14" fill="#ED2939" />
        <rect width="13.33" height="14" fill="#FFFFFF" />
        <rect width="6.67" height="14" fill="#002395" />
      </g>
    </svg>
  );
}

function DEFlag({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id="flag-clip-de">
          <rect width="20" height="14" rx="2" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-clip-de)">
        <rect width="20" height="14" fill="#FFCE00" />
        <rect width="20" height="9.33" fill="#DD0000" />
        <rect width="20" height="4.67" fill="#000000" />
      </g>
    </svg>
  );
}

const localeLabel: Record<Locale, string> = {
  "en-us": "English (US)",
  "en-ca": "English (Canada)",
  "en-au": "English (Australia)",
  "en-gb": "English (UK)",
  "fr-fr": "Français (France)",
  "de-de": "Germany (Deutschland)",
};

const localeFlags: Record<Locale, React.ComponentType<{ className?: string }>> = {
  "en-us": USFlag,
  "en-ca": CAFlag,
  "en-au": AUFlag,
  "en-gb": GBFlag,
  "fr-fr": FRFlag,
  "de-de": DEFlag,
};

function pathSegments(pathname: string): string[] {
  return pathname.split("/").filter(Boolean);
}

/** Locale from first path segment, or null if the path is not locale-prefixed. */
function getLocaleFromPath(pathname: string | null): Locale | null {
  const path = collapseDoubleLocalePrefix(pathname ?? "/");
  const segments = pathSegments(path);
  if (segments.length === 0) return null;
  const normalized = normalizeLocale(segments[0]);
  return isLocale(normalized) ? (normalized as Locale) : null;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length < 2) return null;
  return parts.pop()!.split(";").shift() ?? null;
}

function getLocaleFromCookie(): Locale | null {
  const raw = getCookie("woltrio_locale");
  if (!raw) return null;
  const normalized = normalizeLocale(raw);
  return isLocale(normalized) ? (normalized as Locale) : null;
}

function isPrefixMatch(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function buildPathForLocale(pathname: string, locale: Locale): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  
  // 1. Remove any existing locale segments completely
  const cleanPath = stripLeadingLocaleSegments(path);
  
  // 2. For unlocalized/system routes, we usually don't want to prefix them,
  // but the user might want to switch language and go to home.
  if (
    UNLOCALIZED_PREFIXES.some((p) => isPrefixMatch(cleanPath, p)) ||
    SYSTEM_PREFIXES.some((p) => isPrefixMatch(cleanPath, p))
  ) {
    return `/${locale}`;
  }

  // 3. Some pages are authored for a single locale only — the UK and German
  // city pages have no en-us entry to fall back to, so the same path under
  // another locale renders "not found". Send the user to that locale's
  // homepage rather than to a dead URL.
  const authoredLocale = singleLocaleLocationPaths[stripTrailingSlash(cleanPath)];
  if (authoredLocale && authoredLocale !== locale) {
    return `/${locale}`;
  }

  // 4. Prepend the new locale
  if (cleanPath === "/") return `/${locale}`;
  return `/${locale}${cleanPath}`;
}

/** Short code shown in the compact (navbar) trigger; the flag carries the region. */
const localeShort: Record<Locale, string> = {
  "en-us": "EN",
  "en-ca": "EN",
  "en-au": "EN",
  "en-gb": "EN",
  "fr-fr": "FR",
  "de-de": "DE",
};

export default function LanguageSwitcher({
  label = "Language",
  variant = "default",
}: {
  label?: string;
  /** "default": labelled select (footer, mobile menu). "compact": flag + code pill (navbar). */
  variant?: "default" | "compact";
}) {
  const compact = variant === "compact";
  const router = useRouter();
  const pathname = useVisiblePathname();
  const [current, setCurrent] = React.useState<Locale>(() => {
    const fromPath = getLocaleFromPath(typeof window !== "undefined" ? window.location.pathname : pathname);
    return fromPath ?? defaultLocale;
  });

  React.useLayoutEffect(() => {
    const fromPath = getLocaleFromPath(pathname);
    // If we are on a path with a locale, use it.
    // If not, we are on the default locale (since middleware redirects otherwise).
    setCurrent(fromPath ?? defaultLocale);
  }, [pathname]);

  return (
    <div className="flex items-center gap-3">
      {!compact && (
        <span className="text-xs uppercase tracking-widest text-foreground/60 dark:text-zinc-400">
          {label}
        </span>
      )}
      <Select
        value={current}
        onValueChange={(next) => {
          const locale = next as Locale;
          if (typeof document !== "undefined") {
            document.cookie = `woltrio_locale=${locale}; path=/; max-age=31536000; samesite=lax`;
          }
          setCurrent(locale);
          const pathSource =
            typeof window !== "undefined"
              ? window.location.pathname
              : pathname;
          router.push(buildPathForLocale(pathSource, locale));
          router.refresh();
        }}
      >
        <SelectTrigger
          aria-label={compact ? `${label}: ${localeLabel[current]}` : "Select language"}
          className={
            compact
              ? "h-9 cursor-pointer gap-1.5 rounded-full border-transparent bg-transparent px-2.5 text-xs font-semibold shadow-none hover:bg-foreground/5 focus-visible:ring-orange-500/40 dark:bg-transparent dark:hover:bg-white/10"
              : "bg-muted/40  cursor-pointer border-orange-500/20 hover:bg-muted/60 focus-visible:ring-orange-500/40"
          }
        >
          {compact ? (
            <SelectValue>
              {React.createElement(localeFlags[current], {
                className: "h-3.5 w-5 rounded-[2px] shrink-0 border border-black/10 dark:border-white/15",
              })}
              <span>{localeShort[current]}</span>
            </SelectValue>
          ) : (
            <SelectValue />
          )}
        </SelectTrigger>
        <SelectContent
          className={compact ? "z-[120] border-orange-500/20" : "border-orange-500/20"}
          align={compact ? "end" : undefined}
          data-nosnippet
        >
          {locales.map((loc) => {
            const Flag = localeFlags[loc];
            return (
              <SelectItem key={loc} value={loc} className="cursor-pointer">
                <span className="flex items-center gap-2.5">
                  <Flag className="h-3.5 w-5 rounded-[2px] shrink-0 border border-black/10 dark:border-white/15 shadow-2xs" />
                  <span>{localeLabel[loc]}</span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

